// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export enum SortingDirections {
    Unassigned,
    Ascending,
    Descending
}

export enum GroupDisplays {
    Unassigned,
    Open,
    Closed
}

export enum PinningLocations {
    Unassigned,
    Left,
    Right
}

export enum ColumnTypes {
    String,
    Numeric,
    Boolean
}

export function isGridModel(source: any): source is IGrid {
    // Method Implementation: simple
    return source.modelName === 'Grid';
}

export function isColumnModel(source: any): source is IColumn {
    // Method Implementation: simple
    return source.modelName === 'Column';
}

export function isAnswerModel(source: any): source is IAnswer {
    // Method Implementation: simple
    return source.modelName === 'Answer';
}

export interface GridZodSchemaShape {
    columns?: ColumnZodSchemaShape[];

    childSelector?: string;

    readonly?: string;
}

export interface ColumnZodSchemaShape {
    name?: string;

    displayKey?: string;

    descriptionKey?: string;

    source?: string;

    visible?: string;

    enabled?: string;

    customizable?: string;

    pinned?: 'Unassigned' | 'Left' | 'Right';

    variant?: string;

    group?: string;

    groupDisplay?: 'Unassigned' | 'Open' | 'Closed';

    sortable?: string;

    sortDirection?: 'Unassigned' | 'Ascending' | 'Descending';

    sortIndex?: number;

    type?: 'String' | 'Numeric' | 'Boolean';

    answers?: AnswerZodSchemaShape[];

    fastDataEntry?: string;
}

export interface AnswerZodSchemaShape {
    name?: string;

    optional?: boolean;

    visible?: string;

    enabled?: string;

    displayKey?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const GridZodSchema: Zod.ZodSchema<GridZodSchemaShape> = Zod.lazy(() => Zod.object({
    columns: Zod.array(ColumnZodSchema).optional(),
    childSelector: Zod.string().optional(),
    readonly: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ColumnZodSchema: Zod.ZodSchema<ColumnZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    displayKey: Zod.string().optional(),
    descriptionKey: Zod.string().optional(),
    source: Zod.string().optional(),
    visible: Zod.string().optional(),
    enabled: Zod.string().optional(),
    customizable: Zod.string().optional(),
    pinned: Zod.enum([ 'Unassigned', 'Left', 'Right' ]).optional(),
    variant: Zod.string().optional(),
    group: Zod.string().optional(),
    groupDisplay: Zod.enum([ 'Unassigned', 'Open', 'Closed' ]).optional(),
    sortable: Zod.string().optional(),
    sortDirection: Zod.enum([ 'Unassigned', 'Ascending', 'Descending' ]).optional(),
    sortIndex: Zod.number().optional(),
    type: Zod.enum([ 'String', 'Numeric', 'Boolean' ]).optional(),
    answers: Zod.array(AnswerZodSchema).optional(),
    fastDataEntry: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AnswerZodSchema: Zod.ZodSchema<AnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    optional: Zod.boolean().optional(),
    visible: Zod.string().optional(),
    enabled: Zod.string().optional(),
    displayKey: Zod.string().optional()
}).nonstrict());

export interface IGrid extends Wellspring.IModel {
    readonly modelName: string;

    readonly columns: Column[];

    readonly childSelector: string;

    readonly readonly: string;

    readonly isColumnsDefined: boolean;

    readonly isChildSelectorDefined: boolean;

    readonly isReadonlyDefined: boolean;
}

export interface IColumn extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly displayKey: string;

    readonly descriptionKey: string;

    readonly source: string;

    readonly visible: string;

    readonly enabled: string;

    readonly customizable: string;

    readonly pinned: PinningLocations;

    readonly variant: string;

    readonly group: string;

    readonly groupDisplay: GroupDisplays;

    readonly sortable: string;

    readonly sortDirection: SortingDirections;

    readonly sortIndex: number;

    readonly type: ColumnTypes;

    readonly answers: Answer[];

    readonly fastDataEntry: string;

    readonly isNameDefined: boolean;

    readonly isDisplayKeyDefined: boolean;

    readonly isDescriptionKeyDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isCustomizableDefined: boolean;

    readonly isPinnedDefined: boolean;

    readonly isVariantDefined: boolean;

    readonly isGroupDefined: boolean;

    readonly isGroupDisplayDefined: boolean;

    readonly isSortableDefined: boolean;

    readonly isSortDirectionDefined: boolean;

    readonly isSortIndexDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isAnswersDefined: boolean;

    readonly isFastDataEntryDefined: boolean;
}

export interface IAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly optional: boolean;

    readonly visible: string;

    readonly enabled: string;

    readonly displayKey: string;

    readonly isNameDefined: boolean;

    readonly isOptionalDefined: boolean;

    readonly isVisibleDefined: boolean;

    readonly isEnabledDefined: boolean;

    readonly isDisplayKeyDefined: boolean;
}

export class Grid implements IGrid {
    public static readonly modelName: string = 'Grid';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'columns', 'childSelector', 'readonly' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Grid.modelName;
    }

    private _columns: Column[];

    private _childSelector: string;

    private _readonly: string;

    public get columns(): Column[] {
        // Method Implementation: simple
        return this._columns;
    }

    public get childSelector(): string {
        // Method Implementation: simple
        return this._childSelector;
    }

    public get readonly(): string {
        // Method Implementation: simple
        return this._readonly;
    }

    public set columns(value: Column[]) {
        // Method Implementation: simple
        this._columns = value;
        this._isColumnsDefined = true;
    }

    public set childSelector(value: string) {
        // Method Implementation: simple
        this._childSelector = value;
        this._isChildSelectorDefined = true;
    }

    public set readonly(value: string) {
        // Method Implementation: simple
        this._readonly = value;
        this._isReadonlyDefined = true;
    }

    private _isColumnsDefined: boolean;

    private _isChildSelectorDefined: boolean;

    private _isReadonlyDefined: boolean;

    public get isColumnsDefined(): boolean {
        // Method Implementation: simple
        return this._isColumnsDefined;
    }

    public get isChildSelectorDefined(): boolean {
        // Method Implementation: simple
        return this._isChildSelectorDefined;
    }

    public get isReadonlyDefined(): boolean {
        // Method Implementation: simple
        return this._isReadonlyDefined;
    }

    public constructor();
    public constructor(source: IGrid);
    public constructor(source: Partial<Pick<Grid, 'columns' | 'childSelector' | 'readonly'>>);
    public constructor(source?: IGrid | Partial<Pick<Grid, 'columns' | 'childSelector' | 'readonly'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isGridModel(source)) {
                if (source.isColumnsDefined) {
                    this._columns = source.columns;
                    this._isColumnsDefined = true;
                }
                else {
                    this._columns = [];
                    this._isColumnsDefined = true;
                }
                
                if (source.isChildSelectorDefined) {
                    this._childSelector = source.childSelector;
                    this._isChildSelectorDefined = true;
                }
                else {
                    this._childSelector = '';
                    this._isChildSelectorDefined = false;
                }
                
                if (source.isReadonlyDefined) {
                    this._readonly = source.readonly;
                    this._isReadonlyDefined = true;
                }
                else {
                    this._readonly = '';
                    this._isReadonlyDefined = false;
                }
            }
            else {
                if (source.columns !== undefined) {
                    this._columns = source.columns;
                    this._isColumnsDefined = true;
                }
                else {
                    this._columns = [];
                    this._isColumnsDefined = true;
                }
                
                if (source.childSelector !== undefined) {
                    this._childSelector = source.childSelector;
                    this._isChildSelectorDefined = true;
                }
                else {
                    this._childSelector = '';
                    this._isChildSelectorDefined = false;
                }
                
                if (source.readonly !== undefined) {
                    this._readonly = source.readonly;
                    this._isReadonlyDefined = true;
                }
                else {
                    this._readonly = '';
                    this._isReadonlyDefined = false;
                }
            }
        }
        else {
            this._columns = [];
            this._isColumnsDefined = true;
        
            this._childSelector = '';
            this._isChildSelectorDefined = false;
        
            this._readonly = '';
            this._isReadonlyDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Grid.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isColumnsDefined && this.columns !== undefined)
            definedProperties.push('columns');
        if (this.isChildSelectorDefined && this.childSelector !== undefined)
            definedProperties.push('childSelector');
        if (this.isReadonlyDefined && this.readonly !== undefined)
            definedProperties.push('readonly');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'columns':
                return this.columns;
            case 'childSelector':
                return this.childSelector;
            case 'readonly':
                return this.readonly;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isColumnsDefined && this.columns !== undefined) {
            if (this.columns !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columns', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.columns.length));
                for (let i = 0; i < this.columns.length; i++) {
                    const item = this.columns[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columns', 'null'));
            }
        }
        
        if (this.isChildSelectorDefined && this.childSelector !== undefined) {
            if (this.childSelector !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('childSelector', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.childSelector);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('childSelector', 'null'));
            }
        }
        
        if (this.isReadonlyDefined && this.readonly !== undefined) {
            if (this.readonly !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('readonly', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.readonly);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('readonly', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'columns':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.columns = [];
                
                    let item: Column = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Column.modelName) {
                                item = new Column();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Column.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'columns' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'columns'`);
                        }
                
                        this.columns.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'columns' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'columns'`);
                }
        
                return true;
            case 'childSelector':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.childSelector = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'childSelector' cannot be null`);
                    this.childSelector = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'childSelector'`);
                }
        
                return true;
            case 'readonly':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.readonly = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'readonly' cannot be null`);
                    this.readonly = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'readonly'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // columns
        if (this.isColumnsDefined && this.columns !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.columns === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.columns.length);
            
                for (const item of this.columns) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // childSelector
        if (this.isChildSelectorDefined && this.childSelector !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.childSelector === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.childSelector);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // readonly
        if (this.isReadonlyDefined && this.readonly !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.readonly === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.readonly);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // columns
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Column;
            
                    this.columns = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Column();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.columns.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // childSelector
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.childSelector = <any>null;
                    break;
                case 1:
                    this.childSelector = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // readonly
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.readonly = <any>null;
                    break;
                case 1:
                    this.readonly = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Grid {
        // Method Implementation: simple
        const schema = GridZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: GridZodSchemaShape): Grid {
        // Method Implementation: parseZodSchema
        return new Grid({
            columns: schema.columns?.map(i => Column.parseZodSchema(i)),
            childSelector: schema.childSelector,
            readonly: schema.readonly
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            columns: this.isColumnsDefined ? (this.columns?.map(item => item)) : undefined,
            childSelector: this.isChildSelectorDefined ? ((this.childSelector === null ? undefined : this.childSelector)) : undefined,
            readonly: this.isReadonlyDefined ? ((this.readonly === null ? undefined : this.readonly)) : undefined,
        };
    }

    public clone(): Grid {
        // Method Implementation: clone
        return new Grid({
            columns: this.isColumnsDefined ? this.columns?.map(i => i.clone()) : undefined,
            childSelector: this.isChildSelectorDefined ? this.childSelector : undefined,
            readonly: this.isReadonlyDefined ? this.readonly : undefined
        });
    }
}

export class Column implements IColumn {
    public static readonly modelName: string = 'Column';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'displayKey', 'descriptionKey', 'source', 'visible', 'enabled', 'customizable', 'pinned', 'variant', 'group', 'groupDisplay', 'sortable', 'sortDirection', 'sortIndex', 'type', 'answers', 'fastDataEntry' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Column.modelName;
    }

    private _name: string;

    private _displayKey: string;

    private _descriptionKey: string;

    private _source: string;

    private _visible: string;

    private _enabled: string;

    private _customizable: string;

    private _pinned: PinningLocations;

    private _variant: string;

    private _group: string;

    private _groupDisplay: GroupDisplays;

    private _sortable: string;

    private _sortDirection: SortingDirections;

    private _sortIndex: number;

    private _type: ColumnTypes;

    private _answers: Answer[];

    private _fastDataEntry: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public get descriptionKey(): string {
        // Method Implementation: simple
        return this._descriptionKey;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get visible(): string {
        // Method Implementation: simple
        return this._visible;
    }

    public get enabled(): string {
        // Method Implementation: simple
        return this._enabled;
    }

    public get customizable(): string {
        // Method Implementation: simple
        return this._customizable;
    }

    public get pinned(): PinningLocations {
        // Method Implementation: simple
        return this._pinned;
    }

    public get variant(): string {
        // Method Implementation: simple
        return this._variant;
    }

    public get group(): string {
        // Method Implementation: simple
        return this._group;
    }

    public get groupDisplay(): GroupDisplays {
        // Method Implementation: simple
        return this._groupDisplay;
    }

    public get sortable(): string {
        // Method Implementation: simple
        return this._sortable;
    }

    public get sortDirection(): SortingDirections {
        // Method Implementation: simple
        return this._sortDirection;
    }

    public get sortIndex(): number {
        // Method Implementation: simple
        return this._sortIndex;
    }

    public get type(): ColumnTypes {
        // Method Implementation: simple
        return this._type;
    }

    public get answers(): Answer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public get fastDataEntry(): string {
        // Method Implementation: simple
        return this._fastDataEntry;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    public set descriptionKey(value: string) {
        // Method Implementation: simple
        this._descriptionKey = value;
        this._isDescriptionKeyDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set visible(value: string) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set enabled(value: string) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set customizable(value: string) {
        // Method Implementation: simple
        this._customizable = value;
        this._isCustomizableDefined = true;
    }

    public set pinned(value: PinningLocations) {
        // Method Implementation: simple
        this._pinned = value;
        this._isPinnedDefined = true;
    }

    public set variant(value: string) {
        // Method Implementation: simple
        this._variant = value;
        this._isVariantDefined = true;
    }

    public set group(value: string) {
        // Method Implementation: simple
        this._group = value;
        this._isGroupDefined = true;
    }

    public set groupDisplay(value: GroupDisplays) {
        // Method Implementation: simple
        this._groupDisplay = value;
        this._isGroupDisplayDefined = true;
    }

    public set sortable(value: string) {
        // Method Implementation: simple
        this._sortable = value;
        this._isSortableDefined = true;
    }

    public set sortDirection(value: SortingDirections) {
        // Method Implementation: simple
        this._sortDirection = value;
        this._isSortDirectionDefined = true;
    }

    public set sortIndex(value: number) {
        // Method Implementation: simple
        this._sortIndex = value;
        this._isSortIndexDefined = true;
    }

    public set type(value: ColumnTypes) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set answers(value: Answer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    public set fastDataEntry(value: string) {
        // Method Implementation: simple
        this._fastDataEntry = value;
        this._isFastDataEntryDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    private _isDescriptionKeyDefined: boolean;

    private _isSourceDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isCustomizableDefined: boolean;

    private _isPinnedDefined: boolean;

    private _isVariantDefined: boolean;

    private _isGroupDefined: boolean;

    private _isGroupDisplayDefined: boolean;

    private _isSortableDefined: boolean;

    private _isSortDirectionDefined: boolean;

    private _isSortIndexDefined: boolean;

    private _isTypeDefined: boolean;

    private _isAnswersDefined: boolean;

    private _isFastDataEntryDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public get isDescriptionKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDescriptionKeyDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isCustomizableDefined(): boolean {
        // Method Implementation: simple
        return this._isCustomizableDefined;
    }

    public get isPinnedDefined(): boolean {
        // Method Implementation: simple
        return this._isPinnedDefined;
    }

    public get isVariantDefined(): boolean {
        // Method Implementation: simple
        return this._isVariantDefined;
    }

    public get isGroupDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupDefined;
    }

    public get isGroupDisplayDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupDisplayDefined;
    }

    public get isSortableDefined(): boolean {
        // Method Implementation: simple
        return this._isSortableDefined;
    }

    public get isSortDirectionDefined(): boolean {
        // Method Implementation: simple
        return this._isSortDirectionDefined;
    }

    public get isSortIndexDefined(): boolean {
        // Method Implementation: simple
        return this._isSortIndexDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public get isFastDataEntryDefined(): boolean {
        // Method Implementation: simple
        return this._isFastDataEntryDefined;
    }

    public constructor();
    public constructor(source: IColumn);
    public constructor(source: Partial<Pick<Column, 'name' | 'displayKey' | 'descriptionKey' | 'source' | 'visible' | 'enabled' | 'customizable' | 'pinned' | 'variant' | 'group' | 'groupDisplay' | 'sortable' | 'sortDirection' | 'sortIndex' | 'type' | 'answers' | 'fastDataEntry'>>);
    public constructor(source?: IColumn | Partial<Pick<Column, 'name' | 'displayKey' | 'descriptionKey' | 'source' | 'visible' | 'enabled' | 'customizable' | 'pinned' | 'variant' | 'group' | 'groupDisplay' | 'sortable' | 'sortDirection' | 'sortIndex' | 'type' | 'answers' | 'fastDataEntry'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isColumnModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.isDescriptionKeyDefined) {
                    this._descriptionKey = source.descriptionKey;
                    this._isDescriptionKeyDefined = true;
                }
                else {
                    this._descriptionKey = '';
                    this._isDescriptionKeyDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.isCustomizableDefined) {
                    this._customizable = source.customizable;
                    this._isCustomizableDefined = true;
                }
                else {
                    this._customizable = '';
                    this._isCustomizableDefined = false;
                }
                
                if (source.isPinnedDefined) {
                    this._pinned = source.pinned;
                    this._isPinnedDefined = true;
                }
                else {
                    this._pinned = PinningLocations.Unassigned;
                    this._isPinnedDefined = false;
                }
                
                if (source.isVariantDefined) {
                    this._variant = source.variant;
                    this._isVariantDefined = true;
                }
                else {
                    this._variant = '';
                    this._isVariantDefined = false;
                }
                
                if (source.isGroupDefined) {
                    this._group = source.group;
                    this._isGroupDefined = true;
                }
                else {
                    this._group = '';
                    this._isGroupDefined = false;
                }
                
                if (source.isGroupDisplayDefined) {
                    this._groupDisplay = source.groupDisplay;
                    this._isGroupDisplayDefined = true;
                }
                else {
                    this._groupDisplay = GroupDisplays.Unassigned;
                    this._isGroupDisplayDefined = false;
                }
                
                if (source.isSortableDefined) {
                    this._sortable = source.sortable;
                    this._isSortableDefined = true;
                }
                else {
                    this._sortable = '';
                    this._isSortableDefined = false;
                }
                
                if (source.isSortDirectionDefined) {
                    this._sortDirection = source.sortDirection;
                    this._isSortDirectionDefined = true;
                }
                else {
                    this._sortDirection = SortingDirections.Unassigned;
                    this._isSortDirectionDefined = false;
                }
                
                if (source.isSortIndexDefined) {
                    this._sortIndex = source.sortIndex;
                    this._isSortIndexDefined = true;
                }
                else {
                    this._sortIndex = 0;
                    this._isSortIndexDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ColumnTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.isFastDataEntryDefined) {
                    this._fastDataEntry = source.fastDataEntry;
                    this._isFastDataEntryDefined = true;
                }
                else {
                    this._fastDataEntry = '';
                    this._isFastDataEntryDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
                
                if (source.descriptionKey !== undefined) {
                    this._descriptionKey = source.descriptionKey;
                    this._isDescriptionKeyDefined = true;
                }
                else {
                    this._descriptionKey = '';
                    this._isDescriptionKeyDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.customizable !== undefined) {
                    this._customizable = source.customizable;
                    this._isCustomizableDefined = true;
                }
                else {
                    this._customizable = '';
                    this._isCustomizableDefined = false;
                }
                
                if (source.pinned !== undefined) {
                    this._pinned = source.pinned;
                    this._isPinnedDefined = true;
                }
                else {
                    this._pinned = PinningLocations.Unassigned;
                    this._isPinnedDefined = false;
                }
                
                if (source.variant !== undefined) {
                    this._variant = source.variant;
                    this._isVariantDefined = true;
                }
                else {
                    this._variant = '';
                    this._isVariantDefined = false;
                }
                
                if (source.group !== undefined) {
                    this._group = source.group;
                    this._isGroupDefined = true;
                }
                else {
                    this._group = '';
                    this._isGroupDefined = false;
                }
                
                if (source.groupDisplay !== undefined) {
                    this._groupDisplay = source.groupDisplay;
                    this._isGroupDisplayDefined = true;
                }
                else {
                    this._groupDisplay = GroupDisplays.Unassigned;
                    this._isGroupDisplayDefined = false;
                }
                
                if (source.sortable !== undefined) {
                    this._sortable = source.sortable;
                    this._isSortableDefined = true;
                }
                else {
                    this._sortable = '';
                    this._isSortableDefined = false;
                }
                
                if (source.sortDirection !== undefined) {
                    this._sortDirection = source.sortDirection;
                    this._isSortDirectionDefined = true;
                }
                else {
                    this._sortDirection = SortingDirections.Unassigned;
                    this._isSortDirectionDefined = false;
                }
                
                if (source.sortIndex !== undefined) {
                    this._sortIndex = source.sortIndex;
                    this._isSortIndexDefined = true;
                }
                else {
                    this._sortIndex = 0;
                    this._isSortIndexDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ColumnTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.fastDataEntry !== undefined) {
                    this._fastDataEntry = source.fastDataEntry;
                    this._isFastDataEntryDefined = true;
                }
                else {
                    this._fastDataEntry = '';
                    this._isFastDataEntryDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        
            this._descriptionKey = '';
            this._isDescriptionKeyDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._visible = '';
            this._isVisibleDefined = false;
        
            this._enabled = '';
            this._isEnabledDefined = false;
        
            this._customizable = '';
            this._isCustomizableDefined = false;
        
            this._pinned = PinningLocations.Unassigned;
            this._isPinnedDefined = false;
        
            this._variant = '';
            this._isVariantDefined = false;
        
            this._group = '';
            this._isGroupDefined = false;
        
            this._groupDisplay = GroupDisplays.Unassigned;
            this._isGroupDisplayDefined = false;
        
            this._sortable = '';
            this._isSortableDefined = false;
        
            this._sortDirection = SortingDirections.Unassigned;
            this._isSortDirectionDefined = false;
        
            this._sortIndex = 0;
            this._isSortIndexDefined = false;
        
            this._type = ColumnTypes.String;
            this._isTypeDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        
            this._fastDataEntry = '';
            this._isFastDataEntryDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Column.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined)
            definedProperties.push('descriptionKey');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isCustomizableDefined && this.customizable !== undefined)
            definedProperties.push('customizable');
        if (this.isPinnedDefined && this.pinned !== undefined)
            definedProperties.push('pinned');
        if (this.isVariantDefined && this.variant !== undefined)
            definedProperties.push('variant');
        if (this.isGroupDefined && this.group !== undefined)
            definedProperties.push('group');
        if (this.isGroupDisplayDefined && this.groupDisplay !== undefined)
            definedProperties.push('groupDisplay');
        if (this.isSortableDefined && this.sortable !== undefined)
            definedProperties.push('sortable');
        if (this.isSortDirectionDefined && this.sortDirection !== undefined)
            definedProperties.push('sortDirection');
        if (this.isSortIndexDefined && this.sortIndex !== undefined)
            definedProperties.push('sortIndex');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        if (this.isFastDataEntryDefined && this.fastDataEntry !== undefined)
            definedProperties.push('fastDataEntry');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'displayKey':
                return this.displayKey;
            case 'descriptionKey':
                return this.descriptionKey;
            case 'source':
                return this.source;
            case 'visible':
                return this.visible;
            case 'enabled':
                return this.enabled;
            case 'customizable':
                return this.customizable;
            case 'pinned':
                return this.pinned;
            case 'variant':
                return this.variant;
            case 'group':
                return this.group;
            case 'groupDisplay':
                return this.groupDisplay;
            case 'sortable':
                return this.sortable;
            case 'sortDirection':
                return this.sortDirection;
            case 'sortIndex':
                return this.sortIndex;
            case 'type':
                return this.type;
            case 'answers':
                return this.answers;
            case 'fastDataEntry':
                return this.fastDataEntry;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
        
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined) {
            if (this.descriptionKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('descriptionKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.descriptionKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('descriptionKey', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isCustomizableDefined && this.customizable !== undefined) {
            if (this.customizable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('customizable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.customizable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('customizable', 'null'));
            }
        }
        
        if (this.isPinnedDefined && this.pinned !== undefined) {
            if (this.pinned !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pinned', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.pinned);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pinned', 'null'));
            }
        }
        
        if (this.isVariantDefined && this.variant !== undefined) {
            if (this.variant !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variant', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.variant);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variant', 'null'));
            }
        }
        
        if (this.isGroupDefined && this.group !== undefined) {
            if (this.group !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('group', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.group);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('group', 'null'));
            }
        }
        
        if (this.isGroupDisplayDefined && this.groupDisplay !== undefined) {
            if (this.groupDisplay !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groupDisplay', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.groupDisplay);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groupDisplay', 'null'));
            }
        }
        
        if (this.isSortableDefined && this.sortable !== undefined) {
            if (this.sortable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.sortable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortable', 'null'));
            }
        }
        
        if (this.isSortDirectionDefined && this.sortDirection !== undefined) {
            if (this.sortDirection !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortDirection', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.sortDirection);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortDirection', 'null'));
            }
        }
        
        if (this.isSortIndexDefined && this.sortIndex !== undefined) {
            if (this.sortIndex !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortIndex', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.sortIndex);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sortIndex', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
        
        if (this.isFastDataEntryDefined && this.fastDataEntry !== undefined) {
            if (this.fastDataEntry !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fastDataEntry', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.fastDataEntry);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fastDataEntry', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            case 'descriptionKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.descriptionKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'descriptionKey' cannot be null`);
                    this.descriptionKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'descriptionKey'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.visible = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'visible' cannot be null`);
                    this.visible = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.enabled = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'enabled' cannot be null`);
                    this.enabled = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'customizable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.customizable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'customizable' cannot be null`);
                    this.customizable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'customizable'`);
                }
        
                return true;
            case 'pinned':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.pinned = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'pinned' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'pinned'`);
                }
        
                return true;
            case 'variant':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.variant = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'variant' cannot be null`);
                    this.variant = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variant'`);
                }
        
                return true;
            case 'group':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.group = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'group' cannot be null`);
                    this.group = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'group'`);
                }
        
                return true;
            case 'groupDisplay':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.groupDisplay = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'groupDisplay' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groupDisplay'`);
                }
        
                return true;
            case 'sortable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.sortable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'sortable' cannot be null`);
                    this.sortable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sortable'`);
                }
        
                return true;
            case 'sortDirection':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.sortDirection = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'sortDirection' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sortDirection'`);
                }
        
                return true;
            case 'sortIndex':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.sortIndex = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.sortIndex = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'sortIndex' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sortIndex'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.type = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'type' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: Answer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Answer.modelName) {
                                item = new Answer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Answer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            case 'fastDataEntry':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.fastDataEntry = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'fastDataEntry' cannot be null`);
                    this.fastDataEntry = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fastDataEntry'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // descriptionKey
        if (this.isDescriptionKeyDefined && this.descriptionKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.descriptionKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.descriptionKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.visible === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.visible);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.enabled === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.enabled);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // customizable
        if (this.isCustomizableDefined && this.customizable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.customizable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.customizable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // pinned
        if (this.isPinnedDefined && this.pinned !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.pinned);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // variant
        if (this.isVariantDefined && this.variant !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.variant === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.variant);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // group
        if (this.isGroupDefined && this.group !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.group === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.group);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // groupDisplay
        if (this.isGroupDisplayDefined && this.groupDisplay !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.groupDisplay);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sortable
        if (this.isSortableDefined && this.sortable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.sortable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.sortable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sortDirection
        if (this.isSortDirectionDefined && this.sortDirection !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.sortDirection);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // sortIndex
        if (this.isSortIndexDefined && this.sortIndex !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.sortIndex);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.type);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // fastDataEntry
        if (this.isFastDataEntryDefined && this.fastDataEntry !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.fastDataEntry === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.fastDataEntry);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // descriptionKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.descriptionKey = <any>null;
                    break;
                case 1:
                    this.descriptionKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.visible = <any>null;
                    break;
                case 1:
                    this.visible = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.enabled = <any>null;
                    break;
                case 1:
                    this.enabled = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // customizable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.customizable = <any>null;
                    break;
                case 1:
                    this.customizable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // pinned
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.pinned = reader.readEnumValue();
        }
        
        // variant
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.variant = <any>null;
                    break;
                case 1:
                    this.variant = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // group
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.group = <any>null;
                    break;
                case 1:
                    this.group = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // groupDisplay
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.groupDisplay = reader.readEnumValue();
        }
        
        // sortable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.sortable = <any>null;
                    break;
                case 1:
                    this.sortable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // sortDirection
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.sortDirection = reader.readEnumValue();
        }
        
        // sortIndex
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.sortIndex = reader.readDoubleValue();
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.type = reader.readEnumValue();
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Answer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Answer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // fastDataEntry
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.fastDataEntry = <any>null;
                    break;
                case 1:
                    this.fastDataEntry = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Column {
        // Method Implementation: simple
        const schema = ColumnZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ColumnZodSchemaShape): Column {
        // Method Implementation: parseZodSchema
        return new Column({
            name: schema.name,
            displayKey: schema.displayKey,
            descriptionKey: schema.descriptionKey,
            source: schema.source,
            visible: schema.visible,
            enabled: schema.enabled,
            customizable: schema.customizable,
            pinned: (() => {
            if (!schema.pinned)
                return;
        
            switch (schema.pinned) {
                case 'Unassigned':
                    return PinningLocations.Unassigned;
                case 'Left':
                    return PinningLocations.Left;
                case 'Right':
                    return PinningLocations.Right;
                default:
                    throw new Error(`Enum type "schema.pinned" not recognized`);
            }
        })(),
            variant: schema.variant,
            group: schema.group,
            groupDisplay: (() => {
            if (!schema.groupDisplay)
                return;
        
            switch (schema.groupDisplay) {
                case 'Unassigned':
                    return GroupDisplays.Unassigned;
                case 'Open':
                    return GroupDisplays.Open;
                case 'Closed':
                    return GroupDisplays.Closed;
                default:
                    throw new Error(`Enum type "schema.groupDisplay" not recognized`);
            }
        })(),
            sortable: schema.sortable,
            sortDirection: (() => {
            if (!schema.sortDirection)
                return;
        
            switch (schema.sortDirection) {
                case 'Unassigned':
                    return SortingDirections.Unassigned;
                case 'Ascending':
                    return SortingDirections.Ascending;
                case 'Descending':
                    return SortingDirections.Descending;
                default:
                    throw new Error(`Enum type "schema.sortDirection" not recognized`);
            }
        })(),
            sortIndex: schema.sortIndex,
            type: (() => {
            if (!schema.type)
                return;
        
            switch (schema.type) {
                case 'String':
                    return ColumnTypes.String;
                case 'Numeric':
                    return ColumnTypes.Numeric;
                case 'Boolean':
                    return ColumnTypes.Boolean;
                default:
                    throw new Error(`Enum type "schema.type" not recognized`);
            }
        })(),
            answers: schema.answers?.map(i => Answer.parseZodSchema(i)),
            fastDataEntry: schema.fastDataEntry
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
            descriptionKey: this.isDescriptionKeyDefined ? ((this.descriptionKey === null ? undefined : this.descriptionKey)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            visible: this.isVisibleDefined ? ((this.visible === null ? undefined : this.visible)) : undefined,
            enabled: this.isEnabledDefined ? ((this.enabled === null ? undefined : this.enabled)) : undefined,
            customizable: this.isCustomizableDefined ? ((this.customizable === null ? undefined : this.customizable)) : undefined,
            pinned: this.isPinnedDefined ? (PinningLocations[this.pinned]) : undefined,
            variant: this.isVariantDefined ? ((this.variant === null ? undefined : this.variant)) : undefined,
            group: this.isGroupDefined ? ((this.group === null ? undefined : this.group)) : undefined,
            groupDisplay: this.isGroupDisplayDefined ? (GroupDisplays[this.groupDisplay]) : undefined,
            sortable: this.isSortableDefined ? ((this.sortable === null ? undefined : this.sortable)) : undefined,
            sortDirection: this.isSortDirectionDefined ? (SortingDirections[this.sortDirection]) : undefined,
            sortIndex: this.isSortIndexDefined ? (this.sortIndex) : undefined,
            type: this.isTypeDefined ? (ColumnTypes[this.type]) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
            fastDataEntry: this.isFastDataEntryDefined ? ((this.fastDataEntry === null ? undefined : this.fastDataEntry)) : undefined,
        };
    }

    public clone(): Column {
        // Method Implementation: clone
        return new Column({
            name: this.isNameDefined ? this.name : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined,
            descriptionKey: this.isDescriptionKeyDefined ? this.descriptionKey : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            customizable: this.isCustomizableDefined ? this.customizable : undefined,
            pinned: this.isPinnedDefined ? this.pinned : undefined,
            variant: this.isVariantDefined ? this.variant : undefined,
            group: this.isGroupDefined ? this.group : undefined,
            groupDisplay: this.isGroupDisplayDefined ? this.groupDisplay : undefined,
            sortable: this.isSortableDefined ? this.sortable : undefined,
            sortDirection: this.isSortDirectionDefined ? this.sortDirection : undefined,
            sortIndex: this.isSortIndexDefined ? this.sortIndex : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined,
            fastDataEntry: this.isFastDataEntryDefined ? this.fastDataEntry : undefined
        });
    }
}

export class Answer implements IAnswer {
    public static readonly modelName: string = 'Answer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'optional', 'visible', 'enabled', 'displayKey' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Answer.modelName;
    }

    private _name: string;

    private _optional: boolean;

    private _visible: string;

    private _enabled: string;

    private _displayKey: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get optional(): boolean {
        // Method Implementation: simple
        return this._optional;
    }

    public get visible(): string {
        // Method Implementation: simple
        return this._visible;
    }

    public get enabled(): string {
        // Method Implementation: simple
        return this._enabled;
    }

    public get displayKey(): string {
        // Method Implementation: simple
        return this._displayKey;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set optional(value: boolean) {
        // Method Implementation: simple
        this._optional = value;
        this._isOptionalDefined = true;
    }

    public set visible(value: string) {
        // Method Implementation: simple
        this._visible = value;
        this._isVisibleDefined = true;
    }

    public set enabled(value: string) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    public set displayKey(value: string) {
        // Method Implementation: simple
        this._displayKey = value;
        this._isDisplayKeyDefined = true;
    }

    private _isNameDefined: boolean;

    private _isOptionalDefined: boolean;

    private _isVisibleDefined: boolean;

    private _isEnabledDefined: boolean;

    private _isDisplayKeyDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isOptionalDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionalDefined;
    }

    public get isVisibleDefined(): boolean {
        // Method Implementation: simple
        return this._isVisibleDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public get isDisplayKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isDisplayKeyDefined;
    }

    public constructor();
    public constructor(source: IAnswer);
    public constructor(source: Partial<Pick<Answer, 'name' | 'optional' | 'visible' | 'enabled' | 'displayKey'>>);
    public constructor(source?: IAnswer | Partial<Pick<Answer, 'name' | 'optional' | 'visible' | 'enabled' | 'displayKey'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isAnswerModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isOptionalDefined) {
                    this._optional = source.optional;
                    this._isOptionalDefined = true;
                }
                else {
                    this._optional = false;
                    this._isOptionalDefined = false;
                }
                
                if (source.isVisibleDefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.isDisplayKeyDefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.optional !== undefined) {
                    this._optional = source.optional;
                    this._isOptionalDefined = true;
                }
                else {
                    this._optional = false;
                    this._isOptionalDefined = false;
                }
                
                if (source.visible !== undefined) {
                    this._visible = source.visible;
                    this._isVisibleDefined = true;
                }
                else {
                    this._visible = '';
                    this._isVisibleDefined = false;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
                
                if (source.displayKey !== undefined) {
                    this._displayKey = source.displayKey;
                    this._isDisplayKeyDefined = true;
                }
                else {
                    this._displayKey = '';
                    this._isDisplayKeyDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._optional = false;
            this._isOptionalDefined = false;
        
            this._visible = '';
            this._isVisibleDefined = false;
        
            this._enabled = '';
            this._isEnabledDefined = false;
        
            this._displayKey = '';
            this._isDisplayKeyDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Answer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isOptionalDefined && this.optional !== undefined)
            definedProperties.push('optional');
        if (this.isVisibleDefined && this.visible !== undefined)
            definedProperties.push('visible');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        if (this.isDisplayKeyDefined && this.displayKey !== undefined)
            definedProperties.push('displayKey');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'optional':
                return this.optional;
            case 'visible':
                return this.visible;
            case 'enabled':
                return this.enabled;
            case 'displayKey':
                return this.displayKey;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isOptionalDefined && this.optional !== undefined) {
            if (this.optional !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('optional', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.optional);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('optional', 'null'));
            }
        }
        
        if (this.isVisibleDefined && this.visible !== undefined) {
            if (this.visible !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.visible);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('visible', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
        
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            if (this.displayKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.displayKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('displayKey', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'optional':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.optional = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'optional' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'optional'`);
                }
        
                return true;
            case 'visible':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.visible = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'visible' cannot be null`);
                    this.visible = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'visible'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.enabled = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'enabled' cannot be null`);
                    this.enabled = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            case 'displayKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.displayKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'displayKey' cannot be null`);
                    this.displayKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'displayKey'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // optional
        if (this.isOptionalDefined && this.optional !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.optional);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // visible
        if (this.isVisibleDefined && this.visible !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.visible === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.visible);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.enabled === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.enabled);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // displayKey
        if (this.isDisplayKeyDefined && this.displayKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.displayKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.displayKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // optional
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.optional = reader.readBooleanValue();
        }
        
        // visible
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.visible = <any>null;
                    break;
                case 1:
                    this.visible = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.enabled = <any>null;
                    break;
                case 1:
                    this.enabled = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // displayKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.displayKey = <any>null;
                    break;
                case 1:
                    this.displayKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Answer {
        // Method Implementation: simple
        const schema = AnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AnswerZodSchemaShape): Answer {
        // Method Implementation: parseZodSchema
        return new Answer({
            name: schema.name,
            optional: schema.optional,
            visible: schema.visible,
            enabled: schema.enabled,
            displayKey: schema.displayKey
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            optional: this.isOptionalDefined ? (this.optional) : undefined,
            visible: this.isVisibleDefined ? ((this.visible === null ? undefined : this.visible)) : undefined,
            enabled: this.isEnabledDefined ? ((this.enabled === null ? undefined : this.enabled)) : undefined,
            displayKey: this.isDisplayKeyDefined ? ((this.displayKey === null ? undefined : this.displayKey)) : undefined,
        };
    }

    public clone(): Answer {
        // Method Implementation: clone
        return new Answer({
            name: this.isNameDefined ? this.name : undefined,
            optional: this.isOptionalDefined ? this.optional : undefined,
            visible: this.isVisibleDefined ? this.visible : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined,
            displayKey: this.isDisplayKeyDefined ? this.displayKey : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Grid':
                return new Grid();
            case 'Column':
                return new Column();
            case 'Answer':
                return new Answer();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Grid':
                return new Grid(source);
            case 'Column':
                return new Column(source);
            case 'Answer':
                return new Answer(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Grid':
                return Grid.parse(source);
            case 'Column':
                return Column.parse(source);
            case 'Answer':
                return Answer.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
